import { safeWindow } from './window.ts';

/**
 * Die `Cookie`-Klasse ermöglicht das einfache Arbeiten mit Cookies im Browser.
 *
 * Beispielnutzung:
 *
 * // Erstellen eines Cookie-Objekts
 * const myCookie = new Cookie('userToken');
 *
 * // Lesen des Cookie-Werts
 * const token = myCookie.value;
 * console.log(token); // Gibt den Wert des Cookies 'userToken' zurück oder null, wenn nicht vorhanden.
 *
 * // Setzen eines neuen Cookie-Werts
 * myCookie.value = 'abc123';
 *
 * // Optional: Setzen eines spezifischen Pfads für das Cookie
 * myCookie.path = '/';
 *
 * // Entfernen des Cookies
 * myCookie.remove();
 *
 * Hinweis:
 * - Der Konstruktor akzeptiert optional einen Cookie-String, falls dieser manuell übergeben werden soll.
 * - Standardmäßig wird der Cookie-String aus `document.cookie` gelesen.
 */
export class Cookie {
  private cookieName: string;
  private cookieString: string;
  private cookiePath: string;

  constructor(cookieName: string, cookieString: string = '') {
    this.cookieName = cookieName;

    if (cookieString.length > 0) {
      this.cookieString = cookieString;
    } else {
      this.cookieString = safeWindow()?.document.cookie || '';
    }
  }

  get value(): string | null {
    const cookies = this.cookieString.split(';');

    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i] ?? null;

      if (!cookie) {
        continue;
      }

      const [name, value] = cookie.trim().split('=');

      if (name === this.cookieName) {
        return value ? decodeURIComponent(value.trim()) : null;
      }
    }

    return null;
  }

  set value(newValue: string) {
    this.cookieString = `${this.cookieName}=${encodeURIComponent(newValue)}; path=${this.cookiePath};`;

    const win = safeWindow();

    if (win) {
      win.document.cookie = this.cookieString;
    }
  }

  set path(path: string) {
    this.cookiePath = path;
  }

  remove() {
    this.cookieString = `${this.cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${this.cookiePath};`;

    const win = safeWindow();

    if (win) {
      win.document.cookie = this.cookieString;
    }
  }

  get cookieStringValue(): string {
    return this.cookieString;
  }
}
